import { useEffect, useState, type ReactNode } from 'react';
import { Button } from './Button.js';
import { CalendarSelect } from './CalendarSelect.js';
import { cn } from './cn.js';
import { Field } from './Field.js';
import { Icon } from './Icon.js';
import { MultiSelect } from './MultiSelect.js';
import { Popover, PopoverContent, PopoverTrigger } from './primitives/popover.js';
import { Select } from './Select.js';

/** Uma opção de escolha — a mesma forma nos dois seletores. */
export interface FilterOption {
  value: string;
  label: string;
  /**
   * O grupo da opção numa seleção (`<optgroup>`): opções vizinhas com o mesmo
   * grupo ficam juntas, sob o nome dele. Sem grupo, a opção fica solta.
   */
  group?: string;
}

/** As opções em blocos: um bloco por sequência de vizinhas com o mesmo grupo. */
function optionBlocks(options: readonly FilterOption[]) {
  const blocks: { group: string | undefined; options: FilterOption[] }[] = [];
  for (const option of options) {
    const last = blocks[blocks.length - 1];
    if (last && last.group === option.group) last.options.push(option);
    else blocks.push({ group: option.group, options: [option] });
  }
  return blocks;
}

/**
 * O recorte, como ele viaja entre a tela e o painel.
 *
 * Texto para o que é de um valor só, lista para o que é de vários. Não há
 * booleano: um filtro de ligar e desligar entra como `selecao` de duas opções
 * ("Todos" e "Só as sem consultor"), e o motivo está no cabeçalho de
 * `FilterField` — dentro de um painel, a caixa de marcar solta não diz qual é o
 * outro estado dela.
 */
export type FilterValues = Record<string, string | string[]>;

/** O que todo campo declara, seja qual for o tipo. */
interface FilterFieldCommon {
  label: string;
  /** A frase abaixo do campo — o que o recorte faz, quando não é óbvio. */
  help?: ReactNode;
  disabled?: boolean;
  /**
   * Ocupa as duas colunas da grade.
   *
   * Aqui isso é barato, e no cartão de recortes que este painel substituiu não
   * era: lá a grade era `repeat(auto-fit, minmax(190px, 1fr))`, e um item
   * atravessando todas as trilhas impede o `auto-fit` de colapsar as vazias —
   * os campos de uma célula ficam presos no mínimo com o resto da fileira em
   * branco ao lado. Esta grade tem duas colunas declaradas, então atravessar as
   * duas não desliga nada.
   */
  wide?: boolean;
}

/**
 * Um campo do painel — **sete tipos, e nenhuma saída pelos lados**.
 *
 * ## Por que é uma lista fechada, e não `children`
 *
 * O cartão de recortes que este painel substitui recebia `children`, e cada
 * tela montava os próprios campos. Foi assim que a casa chegou a `w-44`, `w-52`
 * e `w-40` na mesma fileira — medido em 06/09 — e é a mesma doença que a
 * medição de 01/09 achou nas larguras de conteúdo: cada tela isolada estava
 * certa, e o conjunto não.
 *
 * Ele era `apps/web/src/components/FilterCard.tsx`, e **saiu em 18/09**, quando
 * a última das doze telas de lista passou para este painel. A lição dele mora
 * aqui agora; o arquivo não existe mais, e continuar apontando para ele seria
 * mandar quem lê abrir um arquivo apagado.
 *
 * Com a lista fechada, a tela declara **o que** filtrar e o painel decide
 * **como** aquilo se desenha. Um sétimo tipo entra aqui, uma vez, e chega a
 * todas as telas ao mesmo tempo.
 *
 * ## E por que não há `custom: ReactNode`
 *
 * Um encaixe livre traria de volta o que a lista fechada acabou de tirar, com a
 * vantagem de parecer disciplina — o primeiro uso é sempre legítimo, e o quinto
 * é a barra de filtros de novo, com seis desenhos. Quem precisar do sétimo tipo
 * acrescenta o sétimo tipo.
 *
 * ## Nenhum campo desenha controle próprio
 *
 * Todos saem de `Field`, `Select` e `MultiSelect`, que já carregam rótulo
 * visível, alvo de 44px no dedo, estado desabilitado e a borda da marca. Um
 * `<input>` escrito aqui dentro seria o segundo desenho de campo do pacote — e
 * o painel do desenho de 17/09 pede 44px de altura e 16px de curvatura, que são
 * outro campo, não o nosso com outra medida.
 */
export type FilterField =
  /** Texto livre. A busca da tela NÃO entra aqui — ver o cabeçalho de `Filters`. */
  | (FilterFieldCommon & { type: 'texto'; name: string; placeholder?: string })
  /** Uma data (`AAAA-MM-DD`). */
  | (FilterFieldCommon & { type: 'data'; name: string })
  /** Uma competência (`AAAA-MM`). */
  | (FilterFieldCommon & { type: 'periodo'; name: string })
  /** Uma opção entre poucas. A primeira é "todas" e vale vazio — salvo `required`. */
  | (FilterFieldCommon & {
      type: 'selecao';
      name: string;
      options: readonly FilterOption[];
      /** O texto da opção vazia: "Todos", "Todas". */
      placeholder?: string;
      /**
       * Sem opção vazia: escolher é obrigatório.
       *
       * O padrão de um filtro é peneirar uma lista, e "todos" é o estado em que
       * ela nasce. Há campo que não é isso: o painel do time escolhe **qual**
       * quadro a tela desenha, e não existe um quadro de todos os times — a
       * opção vazia ali oferecia uma resposta que o servidor não dá, e o rótulo
       * dela ("O primeiro da lista") descrevia a implementação em vez do que a
       * pessoa veria.
       *
       * Um campo assim **não conta** no distintivo de filtros aplicados: ele
       * está sempre preenchido, e contá-lo faria toda tela abrir dizendo que
       * tem um filtro ligado.
       */
      required?: boolean;
    })
  /**
   * Vários dias ou vários meses, soltos ou em faixa — `CalendarSelect`.
   *
   * O valor é uma LISTA canônica: `['2026-01/2026-03', '2026-07']`. Só use
   * onde a rota aceita a lista; numa tela de competência que apura dinheiro,
   * juntar meses é regra, e não filtro.
   */
  | (FilterFieldCommon & {
      type: 'datas';
      name: string;
      /** `data` para dia, `periodo` para competência. */
      kind?: 'data' | 'periodo';
      /** O texto do gatilho quando nada está marcado — "Todas", "Todos". */
      placeholder?: string;
    })
  /** Várias opções entre muitas, com busca dentro delas. */
  | (FilterFieldCommon & {
      type: 'multipla';
      name: string;
      options: readonly FilterOption[];
      placeholder?: string;
      /** As opções ainda vêm a caminho — "Nada encontrado" mentiria. */
      loading?: boolean;
    })
  /**
   * De/Até, na mesma fileira e sob um nome só.
   *
   * São DUAS chaves em `values` e um campo na tela, como "Data de Criação" na
   * referência. Sem isto cada tela repetia dois `Field` soltos e nomeava o
   * segundo "até" — um rótulo que, lido por leitor de tela fora da fileira, não
   * diz até o quê.
   */
  | (Omit<FilterFieldCommon, 'wide'> & {
      type: 'intervalo';
      from: string;
      to: string;
      /**
       * `data` para dia, `periodo` para competência, `valor` para dinheiro.
       *
       * `valor` entrou em 19/10/2026 com a tabela de boletos em aberto da
       * Carteira ("maior que, menor que"). Os dois campos são texto com teclado
       * decimal e `R$` à frente, e o que chega em `values` é o que foi
       * digitado — quem lê converte com `parseMoneyInput`, de `@evogard/domain`,
       * que é onde a vírgula decimal já é tratada uma vez.
       */
      kind?: 'data' | 'periodo' | 'valor';
    });

/** A lista aceita falsidade: `canSeePeople && { … }` esconde o campo inteiro. */
export type FilterFieldSlot = FilterField | false | null | undefined;

/** As chaves que um campo ocupa em `values` — uma, ou duas no intervalo. */
function keysOf(field: FilterField): string[] {
  return field.type === 'intervalo' ? [field.from, field.to] : [field.name];
}

/** Alguém escolheu alguma coisa aqui? Lista vazia e texto vazio são "não". */
function filled(value: string | string[] | undefined): boolean {
  return Array.isArray(value) ? value.length > 0 : Boolean(value);
}

/**
 * O painel de filtros — o botão, o que ele abre, e o recorte que sai dali.
 *
 * É o desenho de 17/09: gatilho na barra acima da tabela, painel de duas
 * colunas com "Filtrar como:" no alto e, no pé, "Limpar" e "Filtrar". Ele
 * substituiu o cartão de recortes que ficava entre o cabeçalho e a tabela — e
 * em 18/09, com a última tela convertida, aquele cartão foi apagado.
 *
 * ## O rascunho é daqui; o recorte APLICADO é da tela
 *
 * É a única divisão que importa neste arquivo. O painel guarda o que está sendo
 * escolhido e mais nada: quem manda em `values` continua sendo a tela — a mesa
 * de demandas guarda o recorte no ENDEREÇO, e uma cópia viva aqui dentro seria
 * a segunda verdade sobre a mesma pergunta.
 *
 * O rascunho **nasce de novo a cada abertura**. Sem isso, fechar no X e
 * reabrir traria as escolhas descartadas de volta parecendo aplicadas — o
 * mesmo defeito que o `ActionsModal` tem no `useEffect` dele, e que só aparece
 * na segunda abertura, depois de todo teste de caminho feliz.
 *
 * ## Escolher não filtra; "Filtrar" filtra
 *
 * É o que muda em relação ao cartão: lá cada seletor disparava uma consulta ao
 * mudar, e montar um recorte de quatro campos custava quatro requisições e três
 * listas intermediárias que ninguém queria ver. Aqui a tela só é consultada uma
 * vez, no botão — e `Enter` dentro de qualquer campo faz o mesmo, porque o
 * painel é um `<form>` de verdade.
 *
 * ## "Limpar" APLICA o vazio, e é a decisão menos óbvia daqui
 *
 * Ele não zera só as caixas. Um "Limpar" que esvaziasse o rascunho e parasse aí
 * deixaria fechar o painel achando que o recorte foi desfeito, com a tabela
 * ainda recortada — e agora **nada na tela desmente isso**, porque o recorte
 * mudou de casa: ele não está mais visível entre o título e a tabela, está
 * atrás de um botão.
 *
 * É também por isso que o gatilho **conta**. O número ao lado de "Filtros" é o
 * que restou de visível do recorte, e sem ele uma lista de 3 linhas sobre uma
 * base de 4.000 não tem como ser explicada por quem a está olhando.
 *
 * ## A busca fica FORA
 *
 * Ela é da barra (`search`, em `Table`), como na referência: digitar filtra
 * enquanto se digita e não espera botão nenhum. Pôr o texto aqui dentro daria
 * dois campos de busca na mesma tela, ou um que só vale depois de confirmado —
 * e é o único filtro cujo resultado a pessoa usa para decidir o que digitar em
 * seguida.
 */
export function Filters({
  values,
  onApply,
  fields,
  label = 'Filtros',
  align = 'end',
}: {
  /** O recorte que está valendo. Quem o guarda é a tela. */
  values: FilterValues;
  /**
   * O recorte novo, com **todas** as chaves declaradas em `fields` — as vazias
   * como `''` ou `[]`, nunca ausentes. Uma chave que some é uma chave que a
   * tela não sabe que precisa apagar, e o filtro anterior sobreviveria ao
   * "Limpar".
   */
  onApply: (values: FilterValues) => void;
  fields: FilterFieldSlot[];
  /** O rótulo do gatilho, quando "Filtros" não serve. */
  label?: string;
  /** De que lado o painel abre em relação ao gatilho. */
  align?: 'start' | 'end';
}) {
  const declared = fields.filter((f): f is FilterField => Boolean(f));

  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<FilterValues>(values);

  /*
   * O rascunho se refaz na ABERTURA, e não a cada mudança de `values`.
   *
   * Depender de `values` traria a releitura do endereço para dentro do painel:
   * aplicar um filtro reescreve a URL, a URL volta como `values` nova, e o
   * rascunho que a pessoa está editando seria substituído no meio da edição.
   */
  useEffect(() => {
    if (open) setDraft(values);
  }, [open]);

  /** O mapa completo das chaves declaradas, com o que o rascunho tiver. */
  const collected = (source: FilterValues): FilterValues =>
    Object.fromEntries(
      declared.flatMap((field): Array<[string, string | string[]]> =>
        field.type === 'multipla' || field.type === 'datas'
          ? [[field.name, toList(source[field.name])]]
          : keysOf(field).map((key) => [key, toText(source[key])]),
      ),
    );

  /*
   * O campo obrigatório fica de fora da conta, e das duas contas.
   *
   * Ele está sempre preenchido — é o que `required` quer dizer —, e somá-lo
   * faria o distintivo abrir dizendo "1" em toda tela que o declara, e o
   * "Limpar" nascer aceso sem ter o que limpar.
   */
  const counted = declared
    .filter((field) => !(field.type === 'selecao' && field.required === true))
    .flatMap(keysOf);
  const count = counted.filter((key) => filled(values[key])).length;
  const dirty = counted.some((key) => filled(draft[key]));

  const write = (key: string, value: string | string[]) =>
    setDraft((current) => ({ ...current, [key]: value }));

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="contorno"
          icon="filtro"
          /*
           * **Ele veste a tipografia do CAMPO ao lado, e não a do botão.**
           *
           * O `contorno` nasce `font-semibold text-primaria-forte` porque é uma
           * ação — "Exportar", "Arquivar" —, e ação se lê antes do resto. Este
           * não é uma: ele ABRE um painel, como a lupa abre a busca, e na barra
           * ele divide a fileira com o campo de pesquisa. Em negrito e laranja,
           * ao lado de um `placeholder` cinza-claro, ele puxava a fileira
           * inteira para si — o primeiro peso 600 da tela para o controle menos
           * importante dela.
           *
           * `texto-tenue` é a MESMA cor do `placeholder` do campo, e é medida:
           * `theme.test.ts` a cobra a 4.5:1 sobre a superfície nas quatro
           * paletas. Não é um cinza escolhido por parecer bonito.
           *
           * Aberto, ele volta para a cor da primária — é o que a referência faz,
           * e é a única pista de que o painel pertence a ESTE botão: o painel é
           * desenhado num portal, fora da árvore da barra. Desde 17/09 o
           * `border-primaria-texto` daqui repete o que a variante já dá; quem muda ao
           * abrir é o rótulo, que sai do cinza do `placeholder` e volta ao
           * laranja.
           */
          className={cn(
            'font-normal text-texto-tenue',
            open && 'border-primaria-texto text-primaria-texto',
          )}
        >
          {label}
          {count > 0 && (
            <span className="inline-flex min-w-5 items-center justify-center rounded-pilula bg-primaria px-1.5 text-[0.6875rem] font-bold text-primaria-contraste tabular-nums">
              {count}
              {/* "Filtros 3" não diz 3 de quê para quem só ouve a tela */}
              <span className="sr-only"> filtros aplicados</span>
            </span>
          )}
          <Icon nome={open ? 'seta-cima' : 'seta-baixo'} size={16} />
        </Button>
      </PopoverTrigger>

      <PopoverContent
        align={align}
        /* o painel desencosta do gatilho e nunca da borda da janela */
        sideOffset={8}
        /*
         * O respiro de cima é MAIOR que os outros três, e é a faixa do topo.
         *
         * Ela tem 60px de altura mais os 8px da grade da moldura
         * (`Header.tsx`), e no telefone é `sticky` — o gatilho passa POR BAIXO
         * dela ao rolar. Sem este número o painel podia virar para cima e
         * nascer atrás da faixa — aberto, desenhado, e invisível. Os 76px o
         * mantêm sempre abaixo dela. Acima de `lg` a faixa não rola mais junto
         * (a moldura é a janela, e quem rola é a mesa), e o número continua
         * certo porque a faixa ocupa a mesma fatia do alto da tela.
         */
        collisionPadding={{ top: 76, right: 16, bottom: 16, left: 16 }}
        /*
         * 496px, e o teto é o que torna a medida verdadeira num telefone: sem
         * ele o painel nasce mais largo que a janela e a primeira coluna some
         * para fora da tela, do lado em que ele ancora.
         *
         * Eram 624px, a medida da referência, e em uso ela é grande demais: o
         * painel mais comum tem três ou quatro campos, e a largura era escolhida
         * pelo desenho e não pelo conteúdo. Com 496px as duas colunas continuam
         * de pé — cada uma fica com ~230px, acima do mínimo de campo do pacote.
         *
         * **`z-10`, e não o `z-50` do primitivo.** A faixa do topo é `z-20`, e
         * as duas desenham no mesmo contexto de empilhamento: em `z-50` o painel
         * passava POR CIMA dela ao rolar a página, que é a única peça da moldura
         * que nunca pode ser coberta. Abaixo dela, o painel some por trás da
         * faixa como o resto do conteúdo — e continua acima da mesa, que não
         * tem `z` nenhum.
         *
         * **Os sublinhados dentro do `calc` são obrigatórios**, e a versão sem
         * eles foi um defeito medido: `calc(100vw-2rem)` é CSS inválido — o
         * `-` precisa de espaço em volta —, e o Tailwind lê espaço em valor
         * arbitrário como `_`. A classe inteira era descartada, o `p-0` tirava
         * junto o `w-72` do primitivo pelo merge, e o painel nascia SEM largura
         * nenhuma: ele esticava até o tamanho do conteúdo, que num grid de dois
         * `MultiSelect` é o tamanho da tela.
         *
         * São DUAS classes, e não um `min()` com as duas medidas dentro, pelo
         * mesmo motivo: se o teto cair, a largura continua de pé — um valor
         * arbitrário só some inteiro.
         *
         * A curvatura é declarada porque o primitivo do shadcn vem com a dele
         * (`rounded-md`): sem isto, o único painel de 624px da casa teria o
         * canto do menu de 16rem.
         */
        /*
         * **O teto de ALTURA é o espaço que o navegador tem, e ele faltava.**
         *
         * O painel se media sozinho — `min(60vh, 24rem)` no conteúdo, mais 44px
         * de cabeçalho e 60 de pé — e isso ignora onde o gatilho está. Numa
         * janela de 720px, `60vh` são 432: o painel pede 536px de altura, e um
         * gatilho a meia tela não tem 536px nem abaixo nem acima dele. O Radix
         * então EMPURRA o painel para caber, e o que sai da tela é a última
         * parte dele — o pé, com o "Filtrar" dentro.
         *
         * Medido em 18/09 pelo percurso: quatro casos de `internal.sales` com
         * "element is outside of the viewport" por trinta segundos, todos na
         * mesa de vendas, que é o painel de quatro campos. O de três campos do
         * ranking passava — e é por isso que o defeito não aparecia abrindo uma
         * tela só.
         *
         * `--radix-popover-content-available-height` é o que o Radix já mede e
         * publica: a distância entre o gatilho e a borda da janela, descontado
         * o `collisionPadding`. Com o painel preso a ela e o miolo em `flex-1
         * min-h-0`, quem encolhe é a lista de campos e o pé fica onde estava.
         */
        className="z-10 flex max-h-(--radix-popover-content-available-height) w-[31rem] max-w-[calc(100vw_-_2rem)] flex-col rounded-grande p-0"
        aria-label={label}
      >
        <form
          /*
           * `min-h-0` nos dois níveis: sem ele, um filho de flex nunca encolhe
           * abaixo do próprio conteúdo, e o teto de altura do painel não chega
           * ao miolo — o `overflow-y-auto` de baixo nunca rolaria nada.
           */
          className="flex min-h-0 flex-1 flex-col"
          onSubmit={(e) => {
            e.preventDefault();
            onApply(collected(draft));
            setOpen(false);
          }}
        >
          {/*
           * As medidas da moldura: 44px de cabeçalho, 16px de respiro, 12px
           * entre as colunas, 16px entre as fileiras e um pé de 12px. São as da
           * referência apertadas um degrau — ela pedia 55px, 20px, 14px, 24px e
           * 65px, e o painel inteiro ficava alto demais para o que ele pergunta.
           * O teto da rolagem desceu junto, de `60vh` para o menor entre `60vh`
           * e 384px: num monitor alto, `60vh` sozinho é mais de 600px de painel.
           * Elas estão aqui e não nos campos de propósito — o que
           * o painel desenha é a moldura; quem desenha campo é `Field`,
           * `Select` e `MultiSelect`, e reabrir a medida deles aqui dentro
           * daria o segundo campo do pacote.
           */}
          <header className="flex h-11 shrink-0 items-center border-b border-borda px-4">
            <h2 className="text-sm text-texto-suave">Filtrar como:</h2>
          </header>

          {/*
           * A rolagem é do CONTEÚDO, e nunca do painel inteiro: o pé precisa
           * ficar à vista. Um painel de nove campos que rolasse por fora
           * esconderia o "Filtrar" abaixo da dobra, e o botão que conclui o
           * gesto seria o único que se precisa procurar.
           *
           * São DUAS medidas, e as duas fazem falta. `flex-1 min-h-0` é o que
           * cede quando o painel bate no teto do navegador — é ele que cumpre a
           * frase acima, e é o que faltava até 18/09. O `min(60vh, 24rem)` é o
           * teto de gosto para quando há espaço de sobra: num monitor alto, sem
           * ele, um painel de nove campos desenharia 900px de altura porque
           * cabe.
           */}
          <div className="grid min-h-0 max-h-[min(60vh,24rem)] flex-1 grid-cols-1 gap-x-3 gap-y-4 overflow-y-auto p-4 sm:grid-cols-2">
            {declared.map((field) => (
              <div
                key={keysOf(field).join('-')}
                /* o intervalo é largo por construção: são dois campos lado a lado */
                className={cn(
                  (field.type === 'intervalo' || field.wide) && 'sm:col-span-2',
                )}
              >
                <FilterFieldView field={field} draft={draft} write={write} />
              </div>
            ))}
          </div>

          <footer className="flex shrink-0 flex-wrap items-center justify-end gap-3 border-t border-borda px-4 py-3">
            <Button
              variant="contorno"
              icon="fechar"
              disabled={!dirty && count === 0}
              onClick={() => {
                const empty = collected({});
                setDraft(empty);
                /*
                 * Aplica, e o painel FICA ABERTO: limpar costuma ser o primeiro
                 * passo de um recorte novo, e fechar aqui obrigaria a reabrir
                 * para dar o segundo. Quem só queria desfazer já viu o número
                 * do gatilho zerar.
                 */
                onApply(empty);
              }}
            >
              Limpar
            </Button>
            <Button type="submit" icon="confirmado">
              Filtrar
            </Button>
          </footer>
        </form>
      </PopoverContent>
    </Popover>
  );
}

/** O que veio de `values` como texto — lista ali é erro de quem chamou. */
function toText(value: string | string[] | undefined): string {
  return typeof value === 'string' ? value : '';
}

/** E como lista. */
function toList(value: string | string[] | undefined): string[] {
  return Array.isArray(value) ? value : [];
}

function FilterFieldView({
  field,
  draft,
  write,
}: {
  field: FilterField;
  draft: FilterValues;
  write: (key: string, value: string | string[]) => void;
}) {
  switch (field.type) {
    case 'texto':
      return (
        <Field
          label={field.label}
          help={field.help}
          disabled={field.disabled}
          type="text"
          placeholder={field.placeholder}
          value={toText(draft[field.name])}
          onChange={(e) => write(field.name, e.target.value)}
        />
      );

    case 'data':
    case 'periodo':
      return (
        <Field
          label={field.label}
          help={field.help}
          disabled={field.disabled}
          type={field.type === 'data' ? 'date' : 'month'}
          value={toText(draft[field.name])}
          onChange={(e) => write(field.name, e.target.value)}
        />
      );

    case 'selecao':
      return (
        <Select
          label={field.label}
          help={field.help}
          disabled={field.disabled}
          value={toText(draft[field.name])}
          onChange={(e) => write(field.name, e.target.value)}
        >
          {field.required !== true && (
            <option value="">{field.placeholder ?? 'Todos'}</option>
          )}
          {optionBlocks(field.options).map((block, i) => {
            const items = block.options.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ));
            return block.group ? (
              <optgroup key={`${block.group}-${i}`} label={block.group}>
                {items}
              </optgroup>
            ) : (
              items
            );
          })}
        </Select>
      );

    case 'datas':
      return (
        <div className="flex flex-col gap-1.5">
          <CalendarSelect
            label={field.label}
            unit={field.kind === 'periodo' ? 'month' : 'day'}
            placeholder={field.placeholder}
            disabled={field.disabled}
            selected={toList(draft[field.name])}
            onChange={(selected) => write(field.name, selected)}
          />
          {field.help && <p className="text-rotulo text-texto-suave">{field.help}</p>}
        </div>
      );

    case 'multipla':
      return (
        <div className="flex flex-col gap-1.5">
          <MultiSelect
            label={field.label}
            placeholder={field.placeholder}
            options={field.options}
            loading={field.loading}
            disabled={field.disabled}
            selected={toList(draft[field.name])}
            onChange={(selected) => write(field.name, selected)}
          />
          {field.help && <p className="text-rotulo text-texto-suave">{field.help}</p>}
        </div>
      );

    case 'intervalo':
      /*
       * `<fieldset>`/`<legend>`, e não um título solto acima dos dois campos:
       * é a única forma de "De" e "Até" terem um nome comum para quem lê por
       * leitor de tela. Um `<span>` desenharia igual e não seria anunciado em
       * lugar nenhum — o mesmo defeito que a coluna de ação da tabela tinha
       * antes do `hiddenLabel`.
       */
      return (
        <fieldset className="min-w-0">
          <legend className="mb-2 text-xs font-medium text-texto">{field.label}</legend>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {field.kind === 'valor' ? (
              <>
                <Field
                  label="A partir de:"
                  disabled={field.disabled}
                  inputMode="decimal"
                  prefix="R$"
                  placeholder="0,00"
                  value={toText(draft[field.from])}
                  onChange={(e) => write(field.from, e.target.value)}
                />
                <Field
                  label="Até:"
                  disabled={field.disabled}
                  inputMode="decimal"
                  prefix="R$"
                  placeholder="0,00"
                  value={toText(draft[field.to])}
                  onChange={(e) => write(field.to, e.target.value)}
                />
              </>
            ) : (
              <>
                <Field
                  label="De:"
                  disabled={field.disabled}
                  type={field.kind === 'periodo' ? 'month' : 'date'}
                  value={toText(draft[field.from])}
                  onChange={(e) => write(field.from, e.target.value)}
                />
                <Field
                  label="Até:"
                  disabled={field.disabled}
                  type={field.kind === 'periodo' ? 'month' : 'date'}
                  value={toText(draft[field.to])}
                  onChange={(e) => write(field.to, e.target.value)}
                />
              </>
            )}
          </div>
          {field.help && (
            <p className="mt-1.5 text-rotulo text-texto-suave">{field.help}</p>
          )}
        </fieldset>
      );
  }
}
